import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

const ResetPassword = () => {
 useDocumentMetadata(
   'Reset Password — Dakhla',
   'Choose a new secure password for your Dakhla account.'
 );

 const { token } = useParams();
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const role = searchParams.get('role') || 'student';

 const [formData, setFormData] = useState({
 password: '',
 confirmPassword: ''
 });
 const [showPassword, setShowPassword] = useState(false);
 const [showConfirmPassword, setShowConfirmPassword] = useState(false);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState('');
 const [success, setSuccess] = useState('');
 const [validatingToken, setValidatingToken] = useState(true);
 const [tokenValid, setTokenValid] = useState(false);

 useEffect(() => {
 validateToken();
 }, [token, role]);

 const validateToken = async () => {
 try {
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const endpoint = role === 'student'
 ? `${apiUrl}/student/validate-reset-token/${token}`
 : `${apiUrl}/university/validate-reset-token/${token}`;

 const response = await fetch(endpoint);
 const data = await response.json();

 if (response.ok) {
 setTokenValid(true);
 } else {
 setError(data.message || 'Invalid or expired reset token.');
 setTokenValid(false);
 }
 } catch (error) {
 console.error('Token validation error:', error);
 setError('Network error. Please try again.');
 setTokenValid(false);
 } finally {
 setValidatingToken(false);
 }
 };

 const handleChange = (e) => {
 setFormData({
 ...formData,
 [e.target.name]: e.target.value
 });
 if (error) setError('');
 };

 const validatePasswords = () => {
 if (formData.password.length < 6) {
 setError('Password must be at least 6 characters long.');
 return false;
 }
 if (formData.password !== formData.confirmPassword) {
 setError('Passwords do not match.');
 return false;
 }
 return true;
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 setError('');

 if (!validatePasswords()) {
 return;
 }

 setLoading(true);

 try {
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const endpoint = role === 'student'
 ? `${apiUrl}/student/reset-password/${token}`
 : `${apiUrl}/university/reset-password/${token}`;

 const response = await fetch(endpoint, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({
 password: formData.password
 }),
 });

 const data = await response.json();

 if (response.ok) {
 // Show success message and redirect to login after delay
 setError(''); // Clear any previous errors
 setSuccess('Password reset successfully! Redirecting to login...');
 setTimeout(() => {
 navigate('/login');
 }, 2000);
 } else {
 setError(data.message || 'Failed to reset password. Please try again.');
 }
 } catch (error) {
 console.error('Reset password error:', error);
 setError('Network error. Please check your connection and try again.');
 } finally {
 setLoading(false);
 }
 };

 if (validatingToken) {
 return (
 <div className="min-h-screen bg-slate-50 relative flex flex-col justify-center py-12 sm:px-6 lg:px-8 overflow-hidden">
 {/* Background decoration */}
 <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
 <div className="absolute top-0 left-0 w-full h-full opacity-[0.025] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:24px_24px]" />
 </div>
 <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10 px-4 sm:px-0">
 <div className="card bg-white py-8 px-5 rounded-2xl border border-slate-200 shadow-lg text-center">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
 <p className="mt-4 text-sm font-semibold text-slate-500">Validating reset token...</p>
 </div>
 </div>
 </div>
 );
 }

 if (!tokenValid) {
 return (
 <div className="min-h-screen bg-slate-50 relative flex flex-col justify-center py-12 sm:px-6 lg:px-8 overflow-hidden">
 {/* Background decoration */}
 <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
 <div className="absolute top-0 left-0 w-full h-full opacity-[0.025] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:24px_24px]" />
 </div>
 <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10 px-4 sm:px-0">
 <div className="card bg-white py-8 px-5 rounded-2xl border border-slate-200 shadow-lg text-center">
 <div className="p-3.5 bg-red-50 border border-red-200 text-red-600 rounded-xl text-xs font-semibold flex items-center gap-2 mb-4 justify-center">
 <span className="text-sm leading-none">⚠️</span>
 <span>{error}</span>
 </div>
 <p className="text-sm text-slate-500 mb-6 font-medium">
 The password reset link is invalid or has expired.
 </p>
 <button
 onClick={() => navigate('/forgot-password')}
 className="text-primary-600 hover:text-primary-500 text-sm font-bold transition-colors"
 >
 Request a new reset link
 </button>
 </div>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-slate-50 relative flex flex-col justify-center py-12 sm:px-6 lg:px-8 overflow-hidden">
 {/* Background decoration */}
 <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
 <div className="absolute top-0 left-0 w-full h-full opacity-[0.025] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:24px_24px]" />
 <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-primary-500/8 blur-3xl animate-pulse-ring" style={{ animationDuration: '9s' }} />
 <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-indigo-500/8 blur-3xl animate-pulse-ring" style={{ animationDuration: '12s' }} />
 </div>

 <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10 px-4 sm:px-0">
 <div className="text-center">
 <h2 className="text-3xl font-extrabold text-slate-800 tracking-tight">
 Set new password
 </h2>
 <p className="mt-2 text-sm text-slate-500">
 Enter your new password below.
 </p>
 </div>
 </div>

 <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10 px-4 sm:px-0">
 <div className="card relative overflow-hidden bg-white py-8 px-5 sm:rounded-2xl sm:px-10 border border-slate-200 shadow-lg shadow-slate-100">
 {/* Accent line */}

 <form className="space-y-6" onSubmit={handleSubmit}>
 {error && (
 <div className="p-3.5 bg-red-50 border border-red-200 text-red-600 rounded-xl text-xs font-semibold flex items-center gap-2">
 <span className="text-sm leading-none">⚠️</span>
 <span>{error}</span>
 </div>
 )}

 {success && (
 <div className="p-3.5 bg-green-50 border border-green-200 text-green-600 rounded-xl text-xs font-semibold flex items-center gap-2">
 <span className="text-sm leading-none">✅</span>
 <span>{success}</span>
 </div>
 )}

 <div>
 <label htmlFor="password" className="block text-xs font-semibold text-slate-700 mb-1">
 New Password
 </label>
 <div className="relative">
 <input
 id="password"
 name="password"
 type={showPassword ? 'text' : 'password'}
 required
 value={formData.password}
 onChange={handleChange}
 className="appearance-none block w-full px-4 py-2.5 pr-11 bg-white border border-slate-200 rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm"
 placeholder="Enter new password (min 6 characters)"
 />
 <button
 type="button"
 className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
 onClick={() => setShowPassword(!showPassword)}
 >
 {showPassword ? (
 <EyeSlashIcon className="h-4 w-4" />
 ) : (
 <EyeIcon className="h-4 w-4" />
 )}
 </button>
 </div>
 </div>

 <div>
 <label htmlFor="confirmPassword" className="block text-xs font-semibold text-slate-700 mb-1">
 Confirm New Password
 </label>
 <div className="relative">
 <input
 id="confirmPassword"
 name="confirmPassword"
 type={showConfirmPassword ? 'text' : 'password'}
 required
 value={formData.confirmPassword}
 onChange={handleChange}
 className="appearance-none block w-full px-4 py-2.5 pr-11 bg-white border border-slate-200 rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm"
 placeholder="Confirm new password"
 />
 <button
 type="button"
 className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
 onClick={() => setShowConfirmPassword(!showConfirmPassword)}
 >
 {showConfirmPassword ? (
 <EyeSlashIcon className="h-4 w-4" />
 ) : (
 <EyeIcon className="h-4 w-4" />
 )}
 </button>
 </div>
 </div>

 <div>
 <button
 type="submit"
 disabled={loading}
 className={`w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-xl text-sm font-semibold text-white shadow-md active:scale-[0.98] transition-premium cursor-pointer ${
 loading
 ? 'bg-slate-400 cursor-not-allowed shadow-none'
 : 'bg-gradient-to-r from-primary-600 to-indigo-600 hover:from-primary-700 hover:to-indigo-700 shadow-primary-500/20 hover:shadow-lg hover:shadow-primary-500/25 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
 }`}
 >
 {loading ? (
 <>
 <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
 </svg>
 <span>Updating Password...</span>
 </>
 ) : (
 'Update Password'
 )}
 </button>
 </div>
 </form>
 </div>
 </div>
 </div>
 );
};

export default ResetPassword;
